import { useAtomValue } from 'jotai';
import type { RxDocument } from 'rxdb';
import { type ForwardedRef, forwardRef, type ReactNode } from 'react';

import { GenerateText } from '@/components/generate-text';
import { NoteSlashMenu } from '@/components/note-slash-menu';
import { BlockSlashMenu } from '@/components/block-slash-menu';
import { UserSourceSlashMenu } from '@/components/user-source-slash-menu';

import { getTextGenerationKey, textGenerationMapAtom } from '@/state';

import type { Block, Note, UserSource } from '@flintmint/core-shared/rxdb';
import type { SlashMenuProps, SlashMenuRef } from '@/extensions/slash-command';
import type { MenuItemProps } from '@flintmint/web-shared/components/menu-item';

import styles from './slash-menu.module.scss';

export const SlashMenu = forwardRef((props: SlashMenuProps, forwardedRef: ForwardedRef<SlashMenuRef>) => {
  const textGenerationMap = useAtomValue(textGenerationMapAtom);

  const textGenerationKey = getTextGenerationKey({
    documentId: props.editor.storage.documentInfo.documentId,
    editorType: props.editorType,
  });
  const generateTextActive = !!textGenerationMap[textGenerationKey];

  const makeGenerateTextMenuItem: GenerateTextMenuFactory = (cfg = {}) => {
    return {
      name: 'Generate Text',
      type: 'custom-view',
      icon: 'ai-magic',
      headerAction: cfg.headerAction,
      renderer: ({ transitionEnded }: { transitionEnded: boolean }) => (
        <GenerateText llmContext={cfg.llmContext} includeContext={cfg.includeContext} autoFocus={transitionEnded} {...props} />
      ),
    };
  };

  const sharedProps = {
    forwardedRef,
    generateTextActive,
    makeGenerateTextMenuItem,
    className: styles.slashMenu,
  };

  return (() => {
    switch (props.editorType) {
      case 'notes':
        return <NoteSlashMenu {...props} {...sharedProps} />;
      case 'block':
        return <BlockSlashMenu {...props} {...sharedProps} />;
      case 'cortex':
        return <UserSourceSlashMenu {...props} {...sharedProps} />;
      default:
        return null;
    }
  })();
});

type GenerateTextMenuFactory = (cfg?: {
  includeContext?: boolean;
  headerAction?: ReactNode;
  llmContext?: {
    note?: RxDocument<Note>;
    block?: RxDocument<Block>;
    userSource?: RxDocument<UserSource>;
  };
}) => MenuItemProps;

export interface SlashMenuCommonProps extends SlashMenuProps {
  className?: string;
  generateTextActive?: boolean;
  forwardedRef: ForwardedRef<SlashMenuRef>;
  makeGenerateTextMenuItem: GenerateTextMenuFactory;
}
